'use client';

import Image from 'next/image';
import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Link, usePathname } from '@/i18n/routing';
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import LanguageSelector from './selectors/LanguageSelector';
import DarkModeSelector from './selectors/DarkModeSelector';
import { cn } from '@/lib/utils';

export default function Header() {
    const [isOpen, setIsOpen] = useState(false);
    const t = useTranslations('Navbar');
    const pathname = usePathname();

    const isActive = (href: string) => {
        if (href === '/') {
            return pathname === '/';
        }
        return pathname.startsWith(href);
    };
    return (
        <header className="fixed top-0 left-0 right-0 z-50 dark:bg-black/50 bg-white/30  backdrop-blur-lg shadow-sm border-b border-white/20 overflow-hidden">
            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
                <div className="flex items-center min-w-0">
                    {/* Logo */}
                    <div className="flex items-center">
                        <div className="relative">
                            <Link href="/">
                                <Image
                                    src="/paradigma.avif"
                                    alt="Paradigma"
                                    width={80}
                                    height={40}
                                    className="object-contain w-12 h-6 sm:w-16 sm:h-8 lg:w-20 lg:h-10"
                                    priority
                                />
                            </Link>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="hidden lg:flex space-x-3 xl:space-x-4 overflow-hidden ml-4 pt-4">
                        <Link href="/" className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium whitespace-nowrap text-xs xl:text-sm flex-shrink-0", isActive('/') && 'underline font-bold text-purple-800 dark:text-purple-200')}>
                            {t('home')}
                        </Link>
                        <Link href="/ia-en-la-politica" className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium whitespace-nowrap text-xs xl:text-sm flex-shrink-0", isActive('/ia-en-la-politica') && 'underline font-bold text-purple-800 dark:text-purple-200')}>
                            {t('ai_in_politics')}
                        </Link>
                        <Link href="/clon-digital" className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium whitespace-nowrap text-xs xl:text-sm flex-shrink-0", isActive('/clon-digital') && 'underline font-bold text-purple-800 dark:text-purple-200')}>
                            {t('digital_clone')}
                        </Link>
                        <Link href="/termometro-social" className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium whitespace-nowrap text-xs xl:text-sm flex-shrink-0", isActive('/termometro-social') && 'underline font-bold text-purple-800 dark:text-purple-200')}>
                            {t('social_thermometer')}
                        </Link>
                        <Link href="/escudo-digital" className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium whitespace-nowrap text-xs xl:text-sm flex-shrink-0", isActive('/escudo-digital') && 'underline font-bold text-purple-800 dark:text-purple-200')}>
                            {t('digital_shield')}
                        </Link>
                        <Link href="/mapeo-electoral" className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium whitespace-nowrap text-xs xl:text-sm flex-shrink-0", isActive('/mapeo-electoral') && 'underline font-bold text-purple-800 dark:text-purple-200')}>
                            {t('electoral_mapping')}
                        </Link>
                        <Link href="/chatbot" className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium whitespace-nowrap text-xs xl:text-sm flex-shrink-0", isActive('/chatbot') && 'underline font-bold text-purple-800 dark:text-purple-200')}>
                            {t('chatbot')}
                        </Link>
                        <Link href="/caso-argentina" className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium whitespace-nowrap text-xs xl:text-sm flex-shrink-0", isActive('/caso-argentina') && 'underline font-bold text-purple-800 dark:text-purple-200')}>
                            {t('argentina_case')}
                        </Link>
                        <Link href="/capacitacion" className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium whitespace-nowrap text-xs xl:text-sm flex-shrink-0", isActive('/capacitacion') && 'underline font-bold text-purple-800 dark:text-purple-200')}>
                            {t('training')}
                        </Link>
                        <div className="flex items-center gap-2 -mt-2 mb-2">
                            <LanguageSelector />
                            <DarkModeSelector />
                        </div>
                    </nav>

                    {/* Mobile menu button with drawer */}
                    <div className="lg:hidden ml-auto flex">
                        <div className="flex items-center gap-2 mb-2 mt-2">
                            <LanguageSelector />
                            <DarkModeSelector />
                        </div>
                        <Drawer open={isOpen} onOpenChange={setIsOpen}>

                            <DrawerTrigger asChild>
                                <button className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 p-2">
                                    <svg className="h-5 w-5 sm:h-6 sm:w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                                    </svg>
                                </button>
                            </DrawerTrigger>
                            <DrawerContent className="bg-white dark:bg-gray-900">
                                <DrawerHeader>
                                    <DrawerTitle className="text-center">{t('navigation_menu')}</DrawerTitle>
                                </DrawerHeader>
                                <div className="px-4 pb-4">
                                    <nav className="flex flex-col space-y-4">
                                        <Link
                                            href="/"
                                            className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium text-lg py-2 border-b border-gray-200 dark:border-gray-700", isActive('/') && 'underline font-bold text-purple-800 dark:text-purple-200')}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {t('home')}
                                        </Link>
                                        <Link
                                            href="/ia-en-la-politica"
                                            className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium text-lg py-2 border-b border-gray-200 dark:border-gray-700", isActive('/ia-en-la-politica') && 'underline font-bold text-purple-800 dark:text-purple-200')}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {t('ai_in_politics')}
                                        </Link>
                                        <Link
                                            href="/clon-digital"
                                            className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium text-lg py-2 border-b border-gray-200 dark:border-gray-700", isActive('/clon-digital') && 'underline font-bold text-purple-800 dark:text-purple-200')}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {t('digital_clone')}
                                        </Link>
                                        <Link
                                            href="/termometro-social"
                                            className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium text-lg py-2 border-b border-gray-200 dark:border-gray-700", isActive('/termometro-social') && 'underline font-bold text-purple-800 dark:text-purple-200')}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {t('social_thermometer')}
                                        </Link>
                                        <Link
                                            href="/escudo-digital"
                                            className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium text-lg py-2 border-b border-gray-200 dark:border-gray-700", isActive('/escudo-digital') && 'underline font-bold text-purple-800 dark:text-purple-200')}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {t('digital_shield')}
                                        </Link>
                                        <Link
                                            href="/mapeo-electoral"
                                            className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium text-lg py-2 border-b border-gray-200 dark:border-gray-700", isActive('/mapeo-electoral') && 'underline font-bold text-purple-800 dark:text-purple-200')}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {t('electoral_mapping')}
                                        </Link>
                                        <Link
                                            href="/chatbot"
                                            className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium text-lg py-2 border-b border-gray-200 dark:border-gray-700", isActive('/chatbot') && 'underline font-bold text-purple-800 dark:text-purple-200')}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {t('chatbot')}
                                        </Link>
                                        <Link
                                            href="/caso-argentina"
                                            className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium text-lg py-2 border-b border-gray-200 dark:border-gray-700", isActive('/caso-argentina') && 'underline font-bold text-purple-800 dark:text-purple-200')}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {t('argentina_case')}
                                        </Link>
                                        <Link
                                            href="/capacitacion"
                                            className={cn("text-purple-600 dark:text-purple-400 hover:text-purple-800 font-medium text-lg py-2 border-b border-gray-200 dark:border-gray-700", isActive('/capacitacion') && 'underline font-bold text-purple-800 dark:text-purple-200')}
                                            onClick={() => setIsOpen(false)}
                                        >
                                            {t('training')}
                                        </Link>
                                    </nav>
                                </div>
                            </DrawerContent>
                        </Drawer>
                    </div>
                </div>
            </div>
        </header>
    );
}
